import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { eur } from "../lib/format";
import { X, ArrowRight, ArrowLeft, Loader2, Check, MapPin, Calendar, Users, Sparkles, Plus, Compass, Star } from "lucide-react";

// mode: "create" | "edit". booking required for edit. preselectedExperience: tier slug (optional).
export default function JourneyWizard({ mode = "create", booking = null, preselectedExperience = null, preselectedPackage = null, preselectedAccommodation = "shared", onClose, onSaved }) {
  const isEdit = mode === "edit";
  const [experiences, setExperiences] = useState([]);
  const [packages, setPackages] = useState([]);
  const [experienceSlug, setExperienceSlug] = useState(preselectedExperience || booking?.experience_slug || null);
  const [accommodation, setAccommodation] = useState(booking?.accommodation || preselectedAccommodation);
  const [pkg, setPkg] = useState(null);
  const [nights, setNights] = useState(booking?.nights || null);
  const [startDate, setStartDate] = useState(booking?.start_date || "");
  const [guests, setGuests] = useState(booking?.guests || 2);
  const [detours, setDetours] = useState(booking?.detour_slugs || []);
  const [availableDetours, setAvailableDetours] = useState([]);
  const [pricing, setPricing] = useState(null);
  const [step, setStep] = useState(0);
  const [loadingPrice, setLoadingPrice] = useState(false);
  const [saving, setSaving] = useState(false);

  const steps = ["Accommodation", "Destination", "Dates", "Detours", "Review"];
  const reviewStep = steps.length - 1;
  const exp = experiences.find((e) => e.slug === experienceSlug) || null;
  const tomorrow = new Date(Date.now() + 86400000).toISOString().split("T")[0];
  const sortedPackages = [...packages].sort((a, b) => (b.bookable !== false) - (a.bookable !== false));

  useEffect(() => {
    api.get("/experiences").then((r) => {
      setExperiences(r.data);
      const chosen = r.data.find((e) => e.slug === (preselectedExperience || booking?.experience_slug)) || r.data[0];
      if (chosen) { setExperienceSlug(chosen.slug); if (!booking) setNights(chosen.duration_nights); }
    }).catch(() => {});
    api.get("/packages").then((r) => {
      setPackages(r.data);
      if (booking) setPkg(r.data.find((p) => p.slug === booking.package_slug) || null);
      else if (preselectedPackage) setPkg(r.data.find((p) => p.slug === preselectedPackage) || null);
    }).catch(() => {});
  }, [booking]);

  useEffect(() => {
    if (!pkg?.area_slug) { setAvailableDetours([]); return; }
    api.get(`/detours?area=${pkg.area_slug}`).then((r) => setAvailableDetours(r.data)).catch(() => setAvailableDetours([]));
  }, [pkg]);

  useEffect(() => {
    if (step === reviewStep && pkg && experienceSlug) {
      setLoadingPrice(true);
      api.post("/itinerary/preview", { package_slug: pkg.slug, experience_slug: experienceSlug, accommodation, nights: nights || 5, guests, start_date: startDate || null, detours })
        .then((r) => setPricing(r.data.pricing))
        .catch(() => toast.error("Could not calculate price"))
        .finally(() => setLoadingPrice(false));
    }
  }, [step, reviewStep, pkg, experienceSlug, accommodation, guests, startDate, detours, nights]);

  const toggleDetour = (slug) => setDetours((d) => (d.includes(slug) ? d.filter((s) => s !== slug) : [...d, slug]));

  const next = () => {
    const key = steps[step];
    if (key === "Destination" && !pkg) return toast.error("Choose where you'll stay");
    if (key === "Dates" && startDate && startDate < tomorrow) return toast.error("Arrival date must be tomorrow or later");
    setStep((s) => Math.min(s + 1, reviewStep));
  };
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const confirm = async () => {
    setSaving(true);
    try {
      const payload = { package_slug: pkg.slug, experience_slug: experienceSlug, accommodation, nights: nights || exp?.duration_nights, guests, start_date: startDate || null, detours };
      let id;
      if (isEdit) {
        await api.put(`/bookings/${booking.booking_id}`, payload);
        id = booking.booking_id;
        toast.success("Journey updated");
      } else {
        const r = await api.post("/bookings", payload);
        id = r.data.booking_id;
        toast.success("Your journey is designed");
      }
      onSaved(id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save your journey");
    } finally { setSaving(false); }
  };

  const key = steps[step];

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-testid="journey-wizard">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-2xl max-h-[92vh] overflow-y-auto rounded-2xl bg-rr-surface border border-rr-gold/20 shadow-[0_40px_100px_rgba(0,0,0,0.7)]">
        <div className="sticky top-0 z-10 bg-rr-surface/95 backdrop-blur px-6 md:px-8 py-5 border-b border-rr-line flex items-center justify-between">
          <div>
            <span className="overline">{isEdit ? "Edit Journey" : "Design My Journey"}</span>
            <p className="font-heading text-2xl text-rr-ivory mt-0.5">{key}</p>
          </div>
          <button onClick={onClose} data-testid="wizard-close" className="text-rr-muted hover:text-rr-ivory"><X className="w-6 h-6" /></button>
        </div>

        <div className="flex gap-2 px-6 md:px-8 pt-5">
          {steps.map((s, i) => <div key={s} className={`h-1 flex-1 rounded-full transition-colors ${i <= step ? "bg-rr-gold" : "bg-rr-line"}`} />)}
        </div>

        <div className="px-6 md:px-8 py-7">
          {key === "Accommodation" && (
            <div>
              <p className="text-rr-muted text-sm mb-4 flex items-center gap-2"><Sparkles className="w-4 h-4 text-rr-gold" /> {exp?.name || "ROKERT 14"} · {exp?.duration_nights || 14} nights. Choose your accommodation.</p>
              <div className="space-y-3">
                {[
                  { key: "shared", label: "Shared accommodation", price: exp?.price_from_eur, note: "A shared sanctuary room within the programme" },
                  { key: "private", label: "Private accommodation", price: exp?.price_private_eur, note: "Your own private room for the full stay" },
                ].map((opt) => (
                  <button key={opt.key} onClick={() => setAccommodation(opt.key)} data-testid={`wizard-acc-${opt.key}`}
                    className={`w-full text-left rounded-xl border p-5 flex gap-4 items-center transition-all ${accommodation === opt.key ? "border-rr-gold bg-rr-gold/5" : "border-rr-line hover:border-rr-ivory/30"}`}>
                    <div className="flex-1 min-w-0">
                      <p className="font-heading text-xl text-rr-ivory leading-tight">{opt.label}</p>
                      <p className="text-xs text-rr-muted mt-0.5">{opt.note}</p>
                      <p className="text-sm text-rr-gold mt-2">from {eur(opt.price)} <span className="text-rr-muted">per person</span></p>
                    </div>
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${accommodation === opt.key ? "bg-rr-gold text-rr-bg" : "border border-rr-line"}`}>{accommodation === opt.key && <Check className="w-4 h-4" />}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {key === "Destination" && (
            <div>
              <p className="text-rr-muted text-sm mb-4 flex items-center gap-2"><MapPin className="w-4 h-4 text-rr-gold" /> Where would you like your {exp?.name || "retreat"} to unfold?</p>
              <div className="grid sm:grid-cols-2 gap-4">
                {sortedPackages.map((p) => {
                  const available = p.bookable !== false;
                  return (
                    <button key={p.slug} onClick={() => available && setPkg(p)} disabled={!available} aria-disabled={!available}
                      data-testid={`wizard-pkg-${p.slug}`}
                      className={`relative text-left rounded-xl overflow-hidden border transition-all ${!available ? "opacity-45 cursor-not-allowed border-rr-line" : pkg?.slug === p.slug ? "border-rr-gold" : "border-rr-line hover:border-rr-ivory/30"}`}>
                      <div className="h-32 overflow-hidden relative">
                        <img src={p.hero_image} alt="" className={`w-full h-full object-cover ${!available ? "grayscale" : ""}`} />
                        {!available && (
                          <span data-testid={`wizard-pkg-${p.slug}-ribbon`} className="absolute top-3 -right-8 w-32 rotate-45 bg-black/80 text-white text-[10px] uppercase tracking-widest text-center py-1 shadow-lg">
                            Not Available
                          </span>
                        )}
                      </div>
                      <div className="p-4">
                        <div className="flex items-center gap-1.5 text-rr-muted text-xs mb-1"><MapPin className="w-3.5 h-3.5 text-rr-gold" />{p.area}</div>
                        <p className="font-heading text-xl text-rr-ivory leading-tight">{p.name}</p>
                        <p className="text-xs text-rr-muted mt-1 line-clamp-1">{p.tagline}</p>
                        {available && p.early_bird_discount_percent > 0 && (
                          <span data-testid={`wizard-pkg-${p.slug}-earlybird`} className="inline-block mt-2 text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-full bg-rr-gold/15 text-rr-gold border border-rr-gold/30">
                            Early Bird −{p.early_bird_discount_percent}%
                          </span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {key === "Dates" && (
            <div className="space-y-6">
              <div>
                <label className="overline text-rr-muted flex items-center gap-2 mb-3"><Calendar className="w-3.5 h-3.5" /> Arrival Date</label>
                <input type="date" value={startDate} min={tomorrow} onChange={(e) => setStartDate(e.target.value)} data-testid="wizard-date"
                  className="w-full bg-rr-bg border border-rr-line rounded-lg px-4 py-3 text-sm text-rr-ivory focus:border-rr-gold outline-none [color-scheme:dark]" />
                <p className="text-xs text-rr-muted leading-relaxed mt-2.5 flex items-start gap-2" data-testid="wizard-date-note">
                  <Sparkles className="w-3.5 h-3.5 text-rr-gold mt-0.5 shrink-0" />
                  <span>Choose any arrival date with confidence — availability is always assured from our curated collection. Arrange your own flights; we take care of everything from arrival.</span>
                </p>
              </div>
              <div>
                <label className="overline text-rr-muted flex items-center gap-2 mb-3"><Users className="w-3.5 h-3.5" /> Guests</label>
                <div className="flex items-center gap-4">
                  <button onClick={() => setGuests(Math.max(1, guests - 1))} data-testid="wizard-guests-minus" className="w-11 h-11 rounded-full border border-rr-line text-rr-ivory text-lg">−</button>
                  <span className="text-2xl font-heading w-10 text-center text-rr-ivory" data-testid="wizard-guests">{guests}</span>
                  <button onClick={() => setGuests(Math.min(pkg?.guests_max || 8, guests + 1))} data-testid="wizard-guests-plus" className="w-11 h-11 rounded-full border border-rr-line text-rr-ivory text-lg">+</button>
                  <span className="text-xs text-rr-muted ml-2">from {eur(exp?.price_from_eur)} per person</span>
                </div>
              </div>
            </div>
          )}

          {key === "Detours" && (
            <div>
              <p className="text-rr-muted text-sm mb-1 flex items-center gap-2"><Compass className="w-4 h-4 text-rr-gold" /> Optional day journeys from {pkg?.area}</p>
              <p className="text-xs text-rr-muted/70 mb-5">A single day away from your base to discover the island — arranged privately on request. A flat travel fee applies to each.</p>
              {availableDetours.length === 0 ? (
                <p className="text-sm text-rr-muted py-6 text-center">No day journeys are mapped to this base yet.</p>
              ) : (
                <div className="grid sm:grid-cols-2 gap-4">
                  {availableDetours.map((d) => {
                    const active = detours.includes(d.slug);
                    return (
                      <button key={d.slug} onClick={() => toggleDetour(d.slug)} data-testid={`wizard-detour-${d.slug}`}
                        className={`text-left rounded-xl overflow-hidden border transition-all ${active ? "border-rr-gold" : "border-rr-line hover:border-rr-ivory/30"}`}>
                        <div className="relative h-28 overflow-hidden">
                          <img src={d.image} alt="" className="w-full h-full object-cover" />
                          <div className="absolute inset-0 bg-gradient-to-t from-rr-surface/90 to-transparent" />
                          <span className={`absolute top-2.5 right-2.5 w-7 h-7 rounded-full flex items-center justify-center transition-colors ${active ? "bg-rr-gold text-rr-bg" : "bg-rr-bg/70 text-rr-ivory"}`}>
                            {active ? <Check className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                          </span>
                        </div>
                        <div className="p-4">
                          <p className="font-heading text-lg text-rr-ivory leading-tight">{d.name}</p>
                          <p className="text-[11px] text-rr-muted mt-0.5">{d.subtitle}</p>
                          <p className="text-xs text-rr-gold mt-2">{eur(d.flat_price_eur)} <span className="text-rr-muted">flat</span></p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {key === "Review" && (
            <div>
              <div className="rounded-xl border border-rr-line divide-y divide-rr-line">
                <Row label="Programme" value={exp?.name || "—"} />
                <Row label="Accommodation" value={accommodation === "private" ? "Private" : "Shared"} />
                <Row label="Where you'll stay" value={`${pkg?.name} · ${pkg?.area}`} />
                <Row label="Length" value={`${exp?.duration_days} days / ${exp?.duration_nights} nights`} />
                <Row label="Arrival" value={startDate || "To be confirmed"} />
                <Row label="Guests" value={guests} />
                <Row label="Day Detours" value={pricing?.detours?.length ? pricing.detours.map((d) => d.name).join(", ") : "None"} />
              </div>
              <div className="mt-6 rounded-xl bg-rr-forest/50 border border-rr-gold/20 p-6">
                {loadingPrice ? <Loader2 className="w-5 h-5 animate-spin text-rr-gold" /> : (
                  <>
                    <div className="space-y-1.5 mb-4 pb-4 border-b border-rr-line">
                      <div className="flex justify-between text-sm text-rr-muted"><span>{exp?.name} · {guests} {guests === 1 ? "guest" : "guests"}</span><span>{eur(pricing?.stay_total)}</span></div>
                      {pricing?.detour_total > 0 && <div className="flex justify-between text-sm text-rr-muted"><span>Day detours</span><span>{eur(pricing?.detour_total)}</span></div>}
                      {pricing?.early_bird_discount_amount > 0 && (
                        <div className="flex justify-between text-sm text-rr-gold" data-testid="wizard-earlybird-row">
                          <span>Early Bird discount (−{pricing.early_bird_discount_percent}%)</span><span>−{eur(pricing.early_bird_discount_amount)}</span>
                        </div>
                      )}
                    </div>
                    <div className="flex items-end justify-between">
                      <div>
                        <p className="overline">Total</p>
                        {pricing?.early_bird_discount_amount > 0 && (
                          <p className="text-rr-muted line-through text-base mt-1" data-testid="wizard-subtotal">{eur(pricing?.subtotal)}</p>
                        )}
                        <p className="font-heading text-4xl text-rr-ivory mt-1" data-testid="wizard-total">{eur(pricing?.total)}</p>
                        {pricing?.early_bird_discount_amount > 0 && (
                          <span data-testid="wizard-earlybird-badge" className="inline-block mt-2 text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-full bg-rr-gold/15 text-rr-gold border border-rr-gold/30">
                            Early Bird −{pricing.early_bird_discount_percent}%
                          </span>
                        )}
                        <p className="text-xs text-rr-muted mt-1">No payment online — we'll email your invoice</p>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="sticky bottom-0 bg-rr-surface/95 backdrop-blur px-6 md:px-8 py-4 border-t border-rr-line flex items-center justify-between">
          <button onClick={back} disabled={step === 0} data-testid="wizard-back"
            className="flex items-center gap-2 text-sm text-rr-muted hover:text-rr-ivory disabled:opacity-30"><ArrowLeft className="w-4 h-4" /> Back</button>
          {step < reviewStep ? (
            <button onClick={next} data-testid="wizard-next" className="btn-gold px-8 py-3 flex items-center gap-2 text-xs uppercase">Continue <ArrowRight className="w-4 h-4" /></button>
          ) : (
            <button onClick={confirm} disabled={saving || loadingPrice} data-testid="wizard-confirm" className="btn-gold px-8 py-3 flex items-center gap-2 text-xs uppercase">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <>{isEdit ? "Save Changes" : "Create My Journey"} <Check className="w-4 h-4" /></>}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between px-5 py-3.5">
      <span className="text-sm text-rr-muted">{label}</span>
      <span className="text-sm text-rr-ivory capitalize text-right">{value}</span>
    </div>
  );
}
